import { CommonModule } from '@angular/common';
import { Component, OnInit, effect, inject, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ToastService } from 'angular-toastify';
import { Rppk16ClientGroup, Rppk16TableResult } from '../../../../core/models/report/rppk16.model';
import { ClientIInfo } from '../../../../core/models/parking.model';
import { ParkingVehicleTypeResponse } from '../../../../core/models/vehicle.model';
import { MapperRppk16Service } from '../../../../core/mappers/report/mapper-rppk16.service';
import { ApiRppk16Service } from '../../../../core/services/api/report/api-rppk16.service';
import { ApiParkingCardListService } from '../../../../core/services/api/parking/api-parking-card-list.service';
import { ApiService } from '../../../../core/services/api.service';
import { SiteStateService } from '../../../../core/services/site-state.service';
import { downloadReportExcel, buildReportExcelFilename } from '../../../../core/utils/report-excel-export.util';
import { PssLoadingComponent } from '../../../../tools/template/pss/pss-loading/pss-loading.component';
import { ModalInvoicePdfViewerComponent } from '../../modals/finance/invoice/modal-invoice-pdf-viewer/modal-invoice-pdf-viewer.component';

@Component({
  selector: 'app-rppk16',
  standalone: true,
  imports: [CommonModule, FormsModule, PssLoadingComponent, ModalInvoicePdfViewerComponent],
  templateUrl: './rppk16.component.html',
  styleUrl: './rppk16.component.css',
})
export class Rppk16Component implements OnInit {
  readonly siteState = inject(SiteStateService);
  private readonly api = inject(ApiRppk16Service);
  private readonly mapper = inject(MapperRppk16Service);
  private readonly cardApi = inject(ApiParkingCardListService);
  private readonly apiService = inject(ApiService);
  private readonly toast = inject(ToastService);

  isLoading = signal(false);
  isExporting = signal(false);
  isProcessingPdf = signal(false);
  showPdfModal = signal(false);
  pdfSrc: Uint8Array | null = null;
  pdfDownloadName = 'RPPK16.pdf';

  lastFetchedTime: Date | null = null;
  hasSearched = signal(false);
  result = signal<Rppk16TableResult | null>(null);

  clients = signal<ClientIInfo[]>([]);
  clientId: number | null = null;
  searchClientText = '';
  isClientDropdownOpen = false;

  vehicleTypes = signal<ParkingVehicleTypeResponse[]>([]);
  vehicleTypeId: number | null = null;
  searchVehicleTypeText = '';
  isVehicleTypeDropdownOpen = false;

  constructor() {
    effect(() => {
      const siteId = this.siteState.selectedSite()?.id ?? 0;
      untracked(() => {
        this.clientId = null;
        this.searchClientText = '';
        this.clearResult();
        if (siteId > 0) {
          this.loadClients(siteId);
        } else {
          this.clients.set([]);
        }
      });
    });
  }

  ngOnInit(): void {
    this.loadVehicleTypes();
  }

  get selectedSiteId(): number {
    return this.siteState.selectedSite()?.id ?? 0;
  }

  get canSearch(): boolean {
    return this.selectedSiteId > 0;
  }

  get groups(): Rppk16ClientGroup[] {
    return this.result()?.groups ?? [];
  }

  get totals() {
    return this.result()?.totals ?? null;
  }

  get filteredClients(): ClientIInfo[] {
    const q = this.searchClientText.trim().toLowerCase();
    const list = this.clients();
    if (!q || q === 'ทุกบริษัท') return list;
    return list.filter((c) => (c.name || '').toLowerCase().includes(q));
  }

  get filteredVehicleTypes(): ParkingVehicleTypeResponse[] {
    const q = this.searchVehicleTypeText.trim().toLowerCase();
    const list = this.vehicleTypes();
    if (!q || q === 'ทุกประเภทรถ') return list;
    return list.filter((v) => (v.typeName || '').toLowerCase().includes(q));
  }

  onFilterChange(): void {
    this.clearResult();
  }

  selectClient(opt: ClientIInfo): void {
    this.clientId = opt.id;
    this.searchClientText = opt.name || '';
    this.isClientDropdownOpen = false;
    this.clearResult();
  }

  clearClient(): void {
    this.clientId = null;
    this.searchClientText = '';
    this.isClientDropdownOpen = false;
    this.clearResult();
  }

  selectVehicleType(opt: ParkingVehicleTypeResponse): void {
    this.vehicleTypeId = opt.id;
    this.searchVehicleTypeText = opt.typeName || '';
    this.isVehicleTypeDropdownOpen = false;
    this.clearResult();
  }

  clearVehicleType(): void {
    this.vehicleTypeId = null;
    this.searchVehicleTypeText = '';
    this.isVehicleTypeDropdownOpen = false;
    this.clearResult();
  }

  closeDropdowns(): void {
    this.isClientDropdownOpen = false;
    this.isVehicleTypeDropdownOpen = false;
  }

  onSearch(): void {
    if (!this.selectedSiteId) {
      this.toast.error('กรุณาเลือกลานจาก Navbar');
      return;
    }
    this.loadTable();
  }

  onRefresh(): void {
    if (!this.hasSearched()) {
      this.onSearch();
      return;
    }
    this.loadTable();
  }

  private buildQuery() {
    return {
      siteId: this.selectedSiteId,
      clientId: this.clientId,
      vehicleTypeId: this.vehicleTypeId,
    };
  }

  loadTable(): void {
    if (!this.canSearch) {
      this.clearResult();
      return;
    }

    this.isLoading.set(true);
    this.api.getRppk16ForTable(this.buildQuery()).subscribe({
      next: (res) => {
        this.result.set(this.mapper.mapTable(res));
        this.hasSearched.set(true);
        this.lastFetchedTime = new Date();
        this.isLoading.set(false);
      },
      error: (err) => {
        this.result.set(null);
        this.hasSearched.set(true);
        this.isLoading.set(false);
        this.toast.error(err?.error?.message || 'โหลดรายงาน RPPK16 ไม่สำเร็จ');
      },
    });
  }

  async onExportExcel(): Promise<void> {
    if (!this.result() || this.groups.length === 0) {
      this.toast.error('ไม่มีข้อมูลให้ส่งออก');
      return;
    }

    this.isExporting.set(true);
    try {
      const exportRows: Record<string, string | number>[] = [];
      for (const client of this.groups) {
        exportRows.push({
          'บริษัทผู้เช่า': client.clientName,
          'ประเภทรถ': '',
          'ลำดับที่': `รวม ${client.cardCount} ใบ`,
          'ชื่อบัตร': '',
          'ชื่อ - สกุล': '',
          'ทะเบียนรถ': '',
          'ประเภทสมาชิก': '',
          'วันที่เริ่มใช้บัตร': '',
          'วันที่หมดอายุ': '',
        });
        for (const vt of client.vehicleTypes) {
          exportRows.push({
            'บริษัทผู้เช่า': client.clientName,
            'ประเภทรถ': vt.vehicleTypeName,
            'ลำดับที่': `รวม ${vt.cardCount} ใบ`,
            'ชื่อบัตร': '',
            'ชื่อ - สกุล': '',
            'ทะเบียนรถ': '',
            'ประเภทสมาชิก': '',
            'วันที่เริ่มใช้บัตร': '',
            'วันที่หมดอายุ': '',
          });
          for (const c of vt.cards) {
            exportRows.push({
              'บริษัทผู้เช่า': client.clientName,
              'ประเภทรถ': vt.vehicleTypeName,
              'ลำดับที่': c.seq,
              'ชื่อบัตร': c.cardName,
              'ชื่อ - สกุล': c.fullName,
              'ทะเบียนรถ': c.licenses,
              'ประเภทสมาชิก': c.subscriptionName,
              'วันที่เริ่มใช้บัตร': this.formatDateTime(c.issueDate),
              'วันที่หมดอายุ': this.formatDateTime(c.expireDate),
            });
          }
        }
      }

      const t = this.totals!;
      exportRows.push({
        'บริษัทผู้เช่า': 'สรุปรวม',
        'ประเภทรถ': '',
        'ลำดับที่': t.cardCount,
        'ชื่อบัตร': '',
        'ชื่อ - สกุล': '',
        'ทะเบียนรถ': '',
        'ประเภทสมาชิก': '',
        'วันที่เริ่มใช้บัตร': '',
        'วันที่หมดอายุ': '',
      });

      const siteName = this.result()?.siteName || this.siteState.selectedSite()?.name || '—';
      const today = this.toInputDate(new Date());
      await downloadReportExcel(exportRows, {
        reportTitle: 'RPPK16 — รายงานบัตรสมาชิกฟรีตามสิทธิ์',
        siteName,
        siteAddress: this.result()?.siteAddress,
        siteTaxId: this.result()?.siteTaxId,
        startDate: today,
        endDate: today,
        sheetName: 'RPPK16',
        fileName: buildReportExcelFilename('RPPK16', siteName, today, today),
      });
      this.toast.success('ส่งออก Excel สำเร็จ');
    } catch {
      this.toast.error('ส่งออก Excel ไม่สำเร็จ');
    } finally {
      this.isExporting.set(false);
    }
  }

  onPrintPdf(): void {
    if (!this.canSearch) {
      this.toast.error('กรุณาเลือกลาน');
      return;
    }

    this.isProcessingPdf.set(true);
    const siteName = (this.siteState.selectedSite()?.name || 'site').replace(/\s+/g, '_');
    this.pdfDownloadName = `RPPK16_${siteName}.pdf`;

    this.api.getRppk16Pdf(this.buildQuery()).subscribe({
      next: (bytes) => {
        this.pdfSrc = bytes;
        this.showPdfModal.set(true);
        this.isProcessingPdf.set(false);
      },
      error: () => {
        this.toast.error('ไม่สามารถดึงไฟล์ PDF ได้');
        this.isProcessingPdf.set(false);
      },
    });
  }

  closePdfModal(): void {
    this.showPdfModal.set(false);
    this.pdfSrc = null;
  }

  formatDateTime(value: string | null | undefined): string {
    if (!value) return '—';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yyyy = d.getFullYear();
    const hh = String(d.getHours()).padStart(2, '0');
    const mi = String(d.getMinutes()).padStart(2, '0');
    const ss = String(d.getSeconds()).padStart(2, '0');
    return `${dd}/${mm}/${yyyy} ${hh}:${mi}:${ss}`;
  }

  private loadClients(siteId: number): void {
    this.apiService.GetClientIInfo(siteId).subscribe({
      next: (list) => this.clients.set(list ?? []),
      error: () => this.clients.set([]),
    });
  }

  private loadVehicleTypes(): void {
    this.cardApi.getParkingVehicleTypes().subscribe({
      next: (list) => this.vehicleTypes.set(list ?? []),
      error: () => this.vehicleTypes.set([]),
    });
  }

  private clearResult(): void {
    this.result.set(null);
    this.hasSearched.set(false);
    this.lastFetchedTime = null;
  }

  private toInputDate(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }
}
